import { DocumentReference, Entity } from '@marmon/shared/types/common';
import { User } from '@marmon/shared/types/user';

/**
 * Status event types for report workflow transitions.
 */
export const ReportStatusEventTypes = {
	Submitted: 'SUBMITTED',
	ChangesRequested: 'CHANGES_REQUESTED',
	Approved: 'APPROVED',
} as const;

export type ReportStatusEventType =
	(typeof ReportStatusEventTypes)[keyof typeof ReportStatusEventTypes];

/**
 * Previous status types (including DRAFT which is implied).
 */
export const ReportStatusEventFromTypes = {
	Draft: 'DRAFT',
	Submitted: 'SUBMITTED',
	ChangesRequested: 'CHANGES_REQUESTED',
	Approved: 'APPROVED',
} as const;

export type ReportStatusEventFromType =
	(typeof ReportStatusEventFromTypes)[keyof typeof ReportStatusEventFromTypes];

/**
 * Role types for status event creators.
 */
export const ReportStatusEventRoles = {
	Creator: 'CREATOR',
	Reviewer: 'REVIEWER',
} as const;

export type ReportStatusEventRole =
	(typeof ReportStatusEventRoles)[keyof typeof ReportStatusEventRoles];

/**
 * Status event document stored in reports/{reportId}/statusEvents/{eventId}
 * Tracks each status transition in the report workflow.
 */
export interface ReportStatusEvent extends Entity {
	/**
	 * The type of status event (SUBMITTED, CHANGES_REQUESTED, APPROVED)
	 */
	type: ReportStatusEventType;

	/**
	 * The previous status before this transition (DRAFT, SUBMITTED, CHANGES_REQUESTED, APPROVED)
	 */
	fromType: ReportStatusEventFromType;

	/**
	 * The role of the user who created this status event (CREATOR or REVIEWER)
	 */
	role: ReportStatusEventRole;

	/**
	 * Optional comment associated with this status change.
	 * Only one comment per statusEvent (1:1 relationship).
	 */
	comment?: string;

	/**
	 * Reference to the user who created this status event
	 */
	createdBy: DocumentReference<User>;
}
